/**
 * Dashboard Page Component
 *
 * Main dashboard page with tabbed navigation for Metrics, Logs, and Costs.
 * Uses URL-based routing for tab state (following ManagementPage pattern).
 *
 * Data hooks are called at this level so state persists when switching tabs.
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/Auth';
import { Layout } from '@/components/Layout';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { useDashboardCosts } from '@/hooks/useDashboardCosts';
import { useDashboardLogs } from '@/hooks/useDashboardLogs';
import { MetricsTab } from './MetricsTab';
import { LogsTab } from './LogsTab';
import { CostsTab } from './CostsTab';
import { LOG_GROUPS, type TimeRange } from '@/types/dashboard';
import { getLogGroupLabel } from './LogViewer/formatters';

// Get the first log group alphabetically as the default
const DEFAULT_LOG_GROUP = [...LOG_GROUPS].sort((a, b) =>
  getLogGroupLabel(a).localeCompare(getLogGroupLabel(b)),
)[0];

type DashboardTab = 'metrics' | 'logs' | 'costs';

export function DashboardPage() {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Metrics state
  const [metricsTimeRange, setMetricsTimeRange] = useState<TimeRange>('7d');
  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
    lastUpdated: metricsLastUpdated,
  } = useDashboardMetrics(metricsTimeRange);

  // Costs state
  const [costsTimeRange, setCostsTimeRange] = useState<TimeRange>('30d');
  const {
    data: costsData,
    isLoading: costsLoading,
    error: costsError,
    lastUpdated: costsLastUpdated,
  } = useDashboardCosts(costsTimeRange);

  // Logs state
  const [logsTimeRange, setLogsTimeRange] = useState<TimeRange>('1d');
  const [selectedLogGroup, setSelectedLogGroup] =
    useState<string>(DEFAULT_LOG_GROUP);
  const {
    events: logsEvents,
    isLoading: logsLoading,
    isLoadingMore: logsLoadingMore,
    error: logsError,
    hasMore: logsHasMore,
    loadMore: logsLoadMore,
    lastUpdated: logsLastUpdated,
  } = useDashboardLogs(selectedLogGroup, logsTimeRange);

  // Derive active view from URL, defaulting to 'metrics'
  const activeView: DashboardTab =
    tab === 'logs' || tab === 'costs' ? tab : 'metrics';

  const handleTabChange = useCallback(
    (value: string) => {
      if (value) {
        navigate(`/dashboard/${value}`);
      }
    },
    [navigate],
  );

  return (
    <Layout userEmail={user?.email}>
      <div className="flex-1 overflow-auto p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header with ToggleGroup */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-bold tracking-tight">
              System Dashboard
            </h1>
            <ToggleGroup
              type="single"
              value={activeView}
              onValueChange={handleTabChange}
              variant="outline"
            >
              <ToggleGroupItem value="metrics">Metrics</ToggleGroupItem>
              <ToggleGroupItem value="logs">Logs</ToggleGroupItem>
              <ToggleGroupItem value="costs">Costs</ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Tab Content */}
          {activeView === 'metrics' && (
            <MetricsTab
              data={metricsData}
              isLoading={metricsLoading}
              error={metricsError}
              lastUpdated={metricsLastUpdated}
              timeRange={metricsTimeRange}
              onTimeRangeChange={setMetricsTimeRange}
            />
          )}

          {activeView === 'logs' && (
            <LogsTab
              events={logsEvents}
              isLoading={logsLoading}
              isLoadingMore={logsLoadingMore}
              error={logsError}
              hasMore={logsHasMore}
              loadMore={logsLoadMore}
              lastUpdated={logsLastUpdated}
              timeRange={logsTimeRange}
              onTimeRangeChange={setLogsTimeRange}
              selectedLogGroup={selectedLogGroup}
              onLogGroupChange={setSelectedLogGroup}
            />
          )}

          {activeView === 'costs' && (
            <CostsTab
              data={costsData}
              isLoading={costsLoading}
              error={costsError}
              lastUpdated={costsLastUpdated}
              timeRange={costsTimeRange}
              onTimeRangeChange={setCostsTimeRange}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
